import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import Portal from "../Portal";
import {Placement, TooltipRenderPropsLike} from './types';
import {computeTooltipPlacement, isElementVisiblyLaidOut} from './utils';
import {OnboardingTooltip} from "./index";

export type OnboardingSpotlightProps = {
  targetRef: React.RefObject<HTMLElement>;
  message: React.ReactNode;
  onDismiss: () => void; // used for primary action (Next/Finish)
  title?: string;
  variant?: 'border' | 'spotlight';
  stepIndex?: number;
  totalSteps?: number;
  primaryTitle?: string;
  onBack?: () => void;
  onSkip?: () => void;
  onClose?: () => void;
  onCompleteOnly?: () => void; // mark done but do not advance
  preferredPlacements?: Array<'bottom' | 'top' | 'right' | 'left'>;
  type?: 'default' | 'one_time_next';
};

export const OnboardingSpotlight: React.FC<OnboardingSpotlightProps> = ({ 
  targetRef, 
  message, 
  onDismiss, 
  title, 
  variant = 'spotlight', 
  stepIndex = 0, 
  totalSteps = 1, 
  primaryTitle, 
  onBack, 
  onSkip, 
  onClose, 
  onCompleteOnly,
  preferredPlacements = ['bottom', 'top', 'right', 'left'],
  type = 'default' 
}) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const tooltipWrapperRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState<{ width: number; height: number } | null>(null);
  const [visible, setVisible] = useState(true);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    // When the target or step changes, ensure the tooltip is visible/mounted again
    setMounted(true);
    setVisible(true);
    // Smoothly center the next target to reduce perceived jump
    try {
      const el = targetRef?.current as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    } catch {}
  }, [targetRef, stepIndex]);

  // Temporarily elevate the target above TourGuide (z 9999) but below overlay (z 14000)
  useEffect(() => {
    const el = targetRef?.current as HTMLElement | null;
    if (!el) return;

    const prevZ = el.style.zIndex;
    const prevPos = el.style.position;
    const computedPos = window.getComputedStyle(el).position;

    if (computedPos === 'static' && !prevPos) {
      el.style.position = 'relative';
    }
    // Place above TourGuide (9999) and below overlay (14000)
    el.style.zIndex = '10000';

    return () => {
      el.style.zIndex = prevZ;
      el.style.position = prevPos;
    };
  }, [targetRef, stepIndex]);

  useLayoutEffect(() => {
    if (targetRef.current) {
      setRect(targetRef.current.getBoundingClientRect());
    }
  }, [targetRef]);

  useEffect(() => {
    const onResize = () => {
      if (targetRef.current) setRect(targetRef.current.getBoundingClientRect());
      if (tooltipWrapperRef.current) {
        const tr = tooltipWrapperRef.current.getBoundingClientRect();
        setTooltipSize({ width: tr.width, height: tr.height });
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [targetRef]);

  // Broadcast visibility so other UI (like TourGuide) can respond
  useEffect(() => {
    if(visible) {
      try { 
        window.dispatchEvent(new CustomEvent('onboarding:tooltip:open')); 
      }
      catch {}
    }
  }, [visible]);

  const dismissThen = (cb?: () => void) => {
    // Fade out first, then run callback and unmount
    setVisible(false);
    setTimeout(() => {
      try { cb?.(); } catch {}
      setMounted(false);
    }, 160);
  };

  const isLast = stepIndex >= totalSteps - 1;

  const tooltipLikeProps: TooltipRenderPropsLike & { hideStepCount?: boolean } = useMemo(() => {
    const isOneTimeNext = type === 'one_time_next';
    return {
      index: stepIndex,
      size: totalSteps,
      step: { content: message },
      primaryProps: { 
        onClick: () => dismissThen(onDismiss), 
        title: primaryTitle || (isOneTimeNext ? 'Next' : (isLast ? 'Finish' : 'Next')) 
      },
      backProps: onBack ? { onClick: () => dismissThen(onBack), title: 'Back' } : undefined,
      closeProps: { onClick: () => dismissThen(onClose) },
      skipProps: (onSkip && !isOneTimeNext) ? { onClick: () => dismissThen(onSkip), title: 'Skip' } : undefined,
      isLastStep: isLast,
      continuous: false,
      tooltipProps: { style: { pointerEvents: 'auto' } },
      hideStepCount: isOneTimeNext,
    };
  }, [message, onDismiss, onBack, onSkip, onClose, stepIndex, totalSteps, isLast, primaryTitle, type]);

  // Only render when the target is visibly measurable (prevents top-left flashing)
  const targetEl = targetRef.current as HTMLElement | null;
  const targetIsVisiblyLaidOut = isElementVisiblyLaidOut(targetEl);

  if (!targetIsVisiblyLaidOut || !mounted) return null;

  if (!rect) return null;

  const tooltipW = tooltipSize?.width || 420;
  const tooltipH = tooltipSize?.height || 220;
  const { top, left } = computeTooltipPlacement(rect, tooltipW, tooltipH, preferredPlacements as Placement[]);

  const forwardUserLikeClick = (clientX: number, clientY: number) => {
    const overlay = overlayRef.current;
    const prevPointer = overlay?.style.pointerEvents;
    if (overlay) overlay.style.pointerEvents = 'none';
    try {
      const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      if (el) {
        const events = [
          new PointerEvent('pointerdown', { bubbles: true, cancelable: true, pointerType: 'mouse', clientX, clientY }),
          new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window, clientX, clientY }),
          new PointerEvent('pointerup', { bubbles: true, cancelable: true, pointerType: 'mouse', clientX, clientY }),
          new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window, clientX, clientY }),
          new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }),
        ];
        for (const evt of events) {
          try { el.dispatchEvent(evt); } catch {}
        }
      }
    } finally {
      if (overlay) overlay.style.pointerEvents = prevPointer || 'auto';
    }
  };

  const isInsideSpotlight = (clientX: number, clientY: number) => {
    const padding = 6;
    return (
      clientX >= rect.left - padding &&
      clientX <= rect.right + padding &&
      clientY >= rect.top - padding &&
      clientY <= rect.bottom + padding
    );
  };

  const handleOverlayMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!overlayRef.current) return;
    overlayRef.current.style.cursor = isInsideSpotlight(e.clientX, e.clientY) ? 'pointer' : 'default';
  };

  const handleOverlayClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    // Determine if click was inside the spotlight region; if so, forward click then mark step complete
    const x = e.clientX;
    const y = e.clientY;

    if (isInsideSpotlight(x, y)) {
      forwardUserLikeClick(x, y);
      dismissThen(onCompleteOnly || onDismiss);
      return;
    }

    dismissThen(onClose);
  };

  return (
    <Portal>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[14000]"
        onClick={handleOverlayClick}
        onMouseMove={handleOverlayMouseMove}
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 160ms ease' }}
      >
        {variant === 'spotlight' ? (
          <svg className="absolute inset-0 w-full h-full">
            <defs>
              <mask id="onboarding-spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect x={rect.left - 6} y={rect.top - 6} width={rect.width + 12} height={rect.height + 12} rx={8} ry={8} fill="black" />
              </mask>
              {/* Soft glow around the spotlight cutout for dark mode clarity */}
              <filter id="onboarding-spotlight-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="5" />
              </filter>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill={document?.documentElement?.classList?.contains('dark') ? 'rgba(0,0,0,0.72)' : 'rgba(0,0,0,0.6)'} mask="url(#onboarding-spotlight-mask)" />
            {/* Glow halo */}
            <rect
              x={rect.left - 6}
              y={rect.top - 6}
              width={rect.width + 12}
              height={rect.height + 12}
              rx={8}
              ry={8}
              fill="none"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth={4}
              filter="url(#onboarding-spotlight-glow)"
              pointerEvents="none"
            />
            {/* Subtle border to delineate the spotlight area */}
            <rect
              x={rect.left - 6}
              y={rect.top - 6}
              width={rect.width + 12}
              height={rect.height + 12}
              rx={8}
              ry={8}
              fill="none"
              stroke="rgba(255,255,255,0.55)"
              strokeWidth={2}
              pointerEvents="none"
            />
          </svg>
        ) : (
          <div className="fixed inset-0 bg-black/60" />
        )}
      </div>

      {/* Highlight outline when not using cutout */}
      {variant === 'border' && (
        <div
          className="fixed z-[14001] pointer-events-none border-2 border-pink-500 rounded-md"
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipWrapperRef}
        className="fixed z-[14002]"
        style={{
          top,
          left,
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1.0)' : 'scale(0.98)',
          transition: 'opacity 160ms ease, transform 160ms ease'
        }}
      >
        <OnboardingTooltip
          {...(tooltipLikeProps as any)}
        />
      </div>
    </Portal>
  );
};

export default OnboardingSpotlight; 